import { useFetcher } from "@remix-run/react";
import { useCallback, useEffect, useRef } from "react";

import { waitUntil } from "~/utils/helpers";
import { logger } from "~/utils/logging";

import { useDataRefresh } from "./useDataRefresh";
import { useUserContext } from "./useUserContext";

import type { FetcherWithComponents } from "@remix-run/react";

export interface SubmitRouteActionOptions {
  /**
   * Determines if the login prompt should be displayed when
   * attempting the action.
   */
  authRequired: boolean;
}

const fetchers = new Map<string, FetcherWithComponents<any>>();

export class RouteActionError extends Error {}

/**
 * Used to construct a FormData submission.
 */
export function useRouteAction() {
  const fetcher = useFetcher();
  const { currentUser, promptLogin } = useUserContext();
  const { refresh } = useDataRefresh();
  const intentRef = useRef<string>();

  const submitAction = useCallback(
    async <T>(
      data: Record<string, any>,
      options?: SubmitRouteActionOptions,
    ): Promise<T | undefined> => {
      return new Promise((resolve, reject) => {
        // Prompt the user to login if the action requires authentication
        if (options?.authRequired && !currentUser) {
          promptLogin();
          return;
        }

        // Don't attempt if the user is offline
        if (!globalThis.navigator.onLine) {
          return;
        }

        // Allow previous actions to resolve first
        if (intentRef.current && fetchers.has(intentRef.current)) {
          return;
        }

        const intent = (intentRef.current = data.intent);

        const formData = new FormData();

        // Set form data
        Object.keys(data).forEach((key) => {
          const value = data[key];
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else if (value !== undefined) {
            formData.append(key, String(value));
          }
        });

        // Fire the action
        fetcher.submit(formData, { method: "post" });

        // Wait until it completes
        waitUntil(() => {
          return fetchers.get(intent)?.state === "idle";
        })
          .then((data) => {
            // Signal a refresh
            refresh({ intent, formAction: fetchers.get(intent)?.formAction });
            resolve(fetchers.get(intent)?.data);
          })
          .catch((error) => {
            logger.error(
              `Action: ${intent} failed`,
              new RouteActionError(error),
            );
            reject(error);
          })
          .finally(() => {
            fetchers.delete(intent);
            intentRef.current = undefined;
          });
      });
    },
    [currentUser, fetcher, promptLogin, refresh],
  );

  useEffect(() => {
    // Persist the fetcher until it completes so that it
    // can be used to resolve the Promise
    if (intentRef.current) {
      fetchers.set(intentRef.current, fetcher);
    }
  }, [fetcher]);

  return {
    fetcher,
    intent: intentRef.current,
    submitAction,
  };
}
